/**
 * Helper: SearchSpring
 * -----------------------------------------------------------------------------
 * Performs SearchSpring API calls
 *
 */
// eslint-disable-next-line import/no-unresolved
import Cookies from 'js-cookie'
import { v4 as generateUuid } from 'uuid'

import {
  filterItems as DYFilterItemsEvent,
  sortItems as DYSortItemsEvent,
} from '~/helpers/dynamic-yield'
import { klaviyoEvent } from '~/helpers/klaviyo'
import { shopifyProductsByIdQuery } from '~/helpers/shopify'
import localizeUrl from '~/core/helpers/localize-url'
import timings from '~/config/timings'

// 5 minute cache for SS queries
const cacheDurationMs = 300000

/**
 * Gets SS IDs from cookies for a query.
 * Creates the data and adds it to cookies if it's not there.
 * @returns {Object} SS query data
 */
function getSsIdentifiers() {
  let userId = Cookies.get('ssUserId')
  let sessionId = Cookies.get('ssSessionIdNamespace')

  if (!userId) {
    userId = generateUuid()
    Cookies.set('ssUserId', userId)
  }

  if (!sessionId) {
    sessionId = generateUuid()
    Cookies.set('ssSessionIdNamespace', sessionId)
  }

  return {
    userId,
    sessionId,
    pageLoadId: generateUuid(),
    domain: window.location?.origin + window.location?.pathname,
  }
}

/**
 * Searchspring api call. Returns formatted response
 *
 * @param {String} [collectionHandle] - A collection handle to use in bgfilters.
 * @param {String} [endpoint] - 'search', 'trending' or 'autocomplete' endpoint.
 * @param {String} [filters] - A filters object for the query.
 * @param {Boolean} [getShopifyProducts] - Get Shopify product data.
 * @param {Boolean} [isProducts] - If the query is meant to fetch product data.
 * @param {Number} [page] - The page number.
 * @param {String} [query] - Search term.
 * @param {Number} [resultsPerPage] - The number of results per page.
 * @param {String} [siteId] - The SearchSpring site ID to use.
 * @param {String} [redirectResponse] - SS Api query param.
 * @returns {Object}
 *
 */
export async function searchspringApiCall({
  collectionHandle = '',
  endpoint = 'search',
  filters = [],
  getShopifyProducts = false,
  isProducts = true,
  page = 1,
  query = '',
  resultsPerPage = 24,
  siteId = '',
  redirectResponse = 'minimal',
} = {}) {
  // Build url params
  const urlParams = [
    `q=${query}`,
    `siteId=${siteId}`,
    `resultsPerPage=${resultsPerPage}`,
    `page=${page}`,
    isProducts ? `bgfilter.ss_collection=${collectionHandle || ''}` : '',
    isProducts ? 'bgfilter.ss_visibility=collection' : '',
    `redirectResponse=${redirectResponse}`,
    'resultsFormat=json',
    ...filters,
  ]

  // Get results from SS
  const results = await searchSpringQuery({
    siteId,
    endpoint,
    urlParamsArray: urlParams,
  })

  if (results?.redirected && results?.url) {
    window.location = localizeUrl(results.url)
    return {}
  }

  let shopifyResults

  if (isProducts && endpoint !== 'trending') {
    let response = results?.results

    if (endpoint === 'autocomplete') {
      response = results?.autocompleteResults?.results
    }

    if (getShopifyProducts) {
      // Fetch Shopify product data for them
      const ids = response.map((result) => `gid://shopify/Product/${
        result.uid.includes('-')
          ? result.uid.split('-')[0]
          : result.uid
      }`)

      if (ids) {
        shopifyResults = await shopifyProductsByIdQuery(ids, 300000)
      }
    }

    // Delay event tracking
    setTimeout(() => {
      results?.filterSummary?.forEach((filter) => {
        DYFilterItemsEvent(filter)
        klaviyoEvent({ label: `Filter: ${filter.label}` })
      })

      results?.sorting?.options?.forEach((sortOption) => {
        if (!sortOption.active) {
          return
        }

        klaviyoEvent({ label: `Sort: ${sortOption.label}` })
        DYSortItemsEvent(sortOption)
      })
    }, timings.message)
  }

  return {
    searchType: endpoint,
    searchspring: results,
    shopify: shopifyResults,
  }
}

/**
 * Queries the requested SearchSpring API endpoint
 * @param {Object} data.siteId - The SearchSpring site ID to use
 * @param {String} data.endpoint - 'search', 'trending' or 'autocomplete'.
 * @param {Array} data.urlParamsArray - Array of URL params to add to the query
 * @returns {Object} Result
 */
export async function searchSpringQuery({
  siteId,
  endpoint = 'search',
  urlParamsArray,
}) {

  // TODO: Clean up URL params to have an Object passed in
  let urlParams = urlParamsArray.join('&')

  if (!endpoint || !siteId) {
    window.console.error('[SS] Missing the SearchSpring site ID or endpoint.')
    return {}
  }

  const cachedQuery = getCachedSSApiResponse({
    siteId,
    endpoint,
    urlParams,
  })

  if (cachedQuery && cachedQuery.results.length) {
    return cachedQuery
  }

  if (['search', 'autocomplete'].includes(endpoint)) {
    const {
      userId,
      sessionId,
      pageLoadId,
      domain,
    } = getSsIdentifiers()

    urlParams = [
      ...urlParamsArray,
      `userId=${userId}`,
      `sessionId=${sessionId}`,
      `pageLoadId=${pageLoadId}`,
      `domain=${domain}`,
    ].join('&')
  }

  const options = {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  }

  let results = null
  let apiEndpoint = `search/${endpoint}`

  if (endpoint === 'trending') {
    apiEndpoint = 'suggest/trending'
  }

  try {
    const endpointRequestURL = `https://${siteId}.a.searchspring.io/api/${apiEndpoint}?`

    if (endpoint === 'autocomplete') {
      const params = new URLSearchParams(urlParams)

      // Request to Suggest API.
      const suggestResults = await fetch(
        `https://${siteId}.a.searchspring.io/api/suggest/query?${urlParams}`,
        options,
      ).then(response => response.json())

      if (suggestResults?.suggested?.text) {
        params.set('q', suggestResults.suggested.text)
      }

      // Request to Search or Autocomplete API.
      const autocompleteResults = await fetch(
        endpointRequestURL + params.toString(),
        options,
      ).then(response => response.json())

      return {
        autocompleteResults,
        suggestResults,
      }
    }

    // Request to Search or Autocomplete API.
    const endpointRequest = fetch(
      endpointRequestURL + urlParams.toString(),
      options,
    )

    results = await endpointRequest
      .then(response => {
        if (response.redirected) {
          return response
        }

        return response.json()
      })
      .catch(err => window.console.error(err))
  } catch (event) {
    window.console.error('[SS] Error querying the SearchSpring API.', event)
  }

  setCachedSSApiResponse(
    {
      siteId,
      endpoint,
      urlParams,
    },
    results,
  )

  return results
}

/**
 * Looks for a cached SS API response.
 * @param {String} [siteId] - The SS site ID
 * @param {String} [endpoint] - The SS endpoint
 * @param {String} [urlParams] - The URL params
 * @returns {Object|null}
 */
function getCachedSSApiResponse({
  siteId = null,
  endpoint = null,
  urlParams = '',
} = {}) {
  if (!siteId || !endpoint) {
    return null
  }

  let data = null
  const key = `ss--${siteId}-${endpoint}-${urlParams}`
  const cachedData = sessionStorage.getItem(key)

  if (!cachedData) {
    return null
  }

  try {
    data = JSON.parse(cachedData)

    // If cache expired, or bad data, remove it
    if (
      data?.ttl < Date.now() ||
      typeof data?.response !== 'object' ||
      !Object.keys(data?.response).length
    ) {
      sessionStorage.removeItem(key)
      data = null
    }
  } catch (event) {
    // Invalid cached data
    sessionStorage.removeItem(key)
  }

  return data?.response || null
}

/**
 * Sets an SS API response into session storage.
 * @param {String} [siteId] - The SS site ID
 * @param {String} [endpoint] - The SS endpoint
 * @param {String} [urlParams] - The URL params
 * @param {Object} finalResponse - SS Response
 */
function setCachedSSApiResponse(
  {
    siteId = null,
    endpoint = null,
    urlParams = '',
  } = {},
  response = null,
) {
  if (!siteId || !endpoint || !response) {
    return
  }

  const key = `ss--${siteId}-${endpoint}-${urlParams}`
  const cachedResponse = {
    facets: response.facets,
    filterSummary: response.filterSummary,
    merchandising: {
      redirect: response.merchandising?.redirect,
    },
    pagination: response.pagination,
    results: response.results?.map(({
      handle,
      uid,
      ss_tags,
      name,
      product_type_unigram,
      url,
      brand,
      price,
    }) => ({
      handle,
      uid,
      ss_tags,
      name,
      product_type_unigram,
      url,
      brand,
      price,
    })) || [],
    sorting: response.sorting,
  }

  try {
    sessionStorage.setItem(key, JSON.stringify({
      response: cachedResponse,
      ttl: Date.now() + cacheDurationMs,
    }))
  } catch (error) {
    window.console.error('[SS] Error caching query', error, cachedResponse)
  }
}

/**
 * Converts facet data from multiple sources into a final format.
 * @param {Object} data.ssFacet - The SS facet data
 * @param {Object} data.ssSummaryFacet - The SS summary facet data
 * @param {Object} data.liquidFacet - The Liquid facet data
 * @returns {Object} Facet object
 */
export function formatFacet({
  ssFacet,
  ssSummaryFacets,
  liquidFacet,
}) {
  if (!ssFacet?.values) {
    return null
  }

  // Combine data from various sources to create the facet values
  const values = ssFacet?.values?.map((ssFacetValue) => {
    // Facet value is active if found in summary,
    const summaryContainsValue = Boolean(ssSummaryFacets?.find(
      (sum) => sum.field === ssFacet.field &&
        sum.value === ssFacetValue.value,
    ) || false)

    const lFacetValue = liquidFacet?.values?.find((lVal) => lVal.field === ssFacetValue.value)

    const fValue = {
      ...ssFacetValue,
      active: summaryContainsValue || ssFacetValue?.active || false,
      label: lFacetValue?.label || ssFacetValue?.label,
      image: lFacetValue?.image || null,
      type: ssFacet.type,
    }
    return fValue
  })

  // Label from liquid when available
  const facet = {
    ...ssFacet,
    label: liquidFacet?.label || ssFacet?.label,
    isReplacement: liquidFacet?.isReplacement || false,
    values,
  }

  return facet
}

export function formatSearchspringProduct(product) {
  if (!product?.uid) {
    return product
  }

  const data = {
    handle: product.handle,
    id: product.uid.includes('-')
      ? product.uid.split('-')[0]
      : product.uid,
    images: [],
    media: [],
    tags: product.ss_tags || [],
    title: product.name,
    type: product.product_type_unigram || null,
    url: product.url,
    sku: product.sku,
    vendor: product.brand,
  }

  if (cnvs?.store.currency === 'USD' && product?.price) {
    data.price = {
      amount: product.price * 100,
      currency: cnvs.store.currency,
    }
  }

  return data
}
